import { useEffect, useMemo, useRef } from 'react'
import Box from '@/components/common/Box'

import { closeCartDrawer, toggleCartDrawer } from '@/store/uiSlice'
import { useAppDispatch, useAppSelector } from '@/store'
import { selectItemsRemovedCount, clearItemsRemovedCount } from '@/store/ticketSlice'
import { useMenuItems } from '@/pages/menu/hooks/useMenuItems'
import { useLocationRef } from '@/contexts/RefContext'
import { useFormatMessage } from '@/translations/useFormatMessage'
import { OrderDetailsPanel } from '@/components/cart/OrderDetailsPanel'
import { ItemsRemovedBanner } from '@/components/cart/ItemsRemovedBanner'
import {
	DesktopSidebar,
	TabletDimmer,
	TabletModal,
	TabletStickyBar,
	MobileSheet,
	MobileStickyBar,
	SwipeHandle,
	CloseSheetButton,
} from './CartSidebar.styled'

interface CartSidebarProps {
	scheduleId: string
}

export default function CartSidebar({ scheduleId }: CartSidebarProps) {
	const isCartDrawerOpen = useAppSelector((state) => state.ui.isCartDrawerOpen)
	const itemsRemovedCount = useAppSelector(selectItemsRemovedCount)
	const dispatch = useAppDispatch()
	const locationRef = useLocationRef()
	const { t } = useFormatMessage()

	const { data: itemsData } = useMenuItems(locationRef, scheduleId)
	const menuReady = itemsData !== undefined
	const menuItems = useMemo(() => {
		const map: Record<string, NonNullable<typeof itemsData>['items'][number]> = {}
		if (itemsData) {
			for (const item of itemsData.items) {
				map[item.ref] = item
			}
		}
		return map
	}, [itemsData])

	// Refs for WCAG 2.1 AA focus management on the mobile sheet dialog.
	// Pattern mirrors CartBottomBar — focus moves to first focusable element on open,
	// returns to the trigger button on close.
	const mobileSheetRef = useRef<HTMLDivElement>(null)
	const mobileCloseButtonRef = useRef<HTMLButtonElement>(null)
	const mobileStickyBarRef = useRef<HTMLButtonElement>(null)

	// Move focus to the close button when the mobile sheet opens.
	// setTimeout(0) defers until after conditional render mounts the sheet DOM.
	useEffect(() => {
		if (!isCartDrawerOpen) return
		const id = setTimeout(() => {
			mobileCloseButtonRef.current?.focus()
		}, 0)
		return () => clearTimeout(id)
	}, [isCartDrawerOpen])

	// Return focus to the sticky bar trigger when the sheet closes.
	// Must be an effect — MobileStickyBar remounts after isCartDrawerOpen flips to false,
	// so the ref is null if we try to focus synchronously inside handleCloseMobileSheet.
	const prevCartDrawerOpen = useRef(false)
	useEffect(() => {
		if (prevCartDrawerOpen.current && !isCartDrawerOpen) {
			mobileStickyBarRef.current?.focus()
		}
		prevCartDrawerOpen.current = isCartDrawerOpen
	}, [isCartDrawerOpen])

	useEffect(() => {
		if (!isCartDrawerOpen) return
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape') dispatch(closeCartDrawer())
		}
		document.addEventListener('keydown', handleEscape)
		return () => document.removeEventListener('keydown', handleEscape)
	}, [isCartDrawerOpen, dispatch])

	function handleCloseMobileSheet() {
		dispatch(closeCartDrawer())
		// Focus restore is handled by the isCartDrawerOpen effect above —
		// MobileStickyBar remounts after the state flip so the ref is not yet available here.
	}

	// Focus trap — keep Tab/Shift+Tab cycling within the mobile sheet (WCAG 2.1 AA).
	// aria-modal="true" prevents AT from reaching outside content but does not
	// physically constrain keyboard focus — we must do that ourselves.
	// Escape is intentionally omitted here — the document-level useEffect above handles it,
	// avoiding a double dispatch when both handlers would otherwise fire.
	function handleMobileSheetKeyDown(e: React.KeyboardEvent) {
		if (e.key !== 'Tab') return

		const sheet = mobileSheetRef.current
		if (!sheet) return

		const focusable = Array.from(
			sheet.querySelectorAll<HTMLElement>(
				'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
			),
		).filter((el) => !el.hasAttribute('disabled'))

		if (focusable.length === 0) return

		const first = focusable[0]
		const last = focusable[focusable.length - 1]

		if (e.shiftKey) {
			if (document.activeElement === first) {
				e.preventDefault()
				last.focus()
			}
		} else {
			if (document.activeElement === last) {
				e.preventDefault()
				first.focus()
			}
		}
	}

	const banner =
		itemsRemovedCount > 0 ? (
			<ItemsRemovedBanner
				count={itemsRemovedCount}
				onDismiss={() => dispatch(clearItemsRemovedCount())}
			/>
		) : null

	return (
		<>
			{/* Desktop — fills the sticky wrapper height; items list scrolls inside */}
			<DesktopSidebar
				data-testid="cart-sidebar-desktop"
				sx={{ display: { xs: 'none', lg: 'flex' } }}
			>
				{banner}
				{menuReady && <OrderDetailsPanel menuItems={menuItems} />}
			</DesktopSidebar>

			{/* Tablet — bottom card (closed) or centred modal over dimmer (open) */}
			<Box
				data-testid="cart-sidebar-tablet"
				sx={{ display: { xs: 'none', md: 'block', lg: 'none' } }}
			>
				{/* Dimmer — always in DOM; visible only when drawer is open */}
				<TabletDimmer
					role="dialog"
					aria-modal="true"
					aria-hidden={!isCartDrawerOpen}
					aria-label={t('component:orderDetailsPanel:heading:label')}
					data-testid="cart-dimmer"
					isOpen={isCartDrawerOpen}
					onClick={() => dispatch(closeCartDrawer())}
				>
					<TabletModal onClick={(e) => e.stopPropagation()}>
						{banner}
						{menuReady && <OrderDetailsPanel menuItems={menuItems} />}
					</TabletModal>
				</TabletDimmer>

				{/* Bottom sticky bar — always in DOM; visible only when drawer is closed */}
				<TabletStickyBar
					data-testid="cart-sticky-bar-tablet"
					isOpen={isCartDrawerOpen}
					aria-label={t('page:home:reviewOrder:text')}
					aria-haspopup="dialog"
					onClick={() => dispatch(toggleCartDrawer())}
				>
					{t('page:home:reviewOrder:text')}
				</TabletStickyBar>
			</Box>

			{/* Mobile — full-width bottom bar (closed) or bottom sheet (open) */}
			<Box data-testid="cart-sidebar-mobile" sx={{ display: { xs: 'block', md: 'none' } }}>
				{isCartDrawerOpen ? (
					<MobileSheet
						ref={mobileSheetRef}
						role="dialog"
						aria-modal="true"
						aria-label={t('component:orderDetailsPanel:heading:label')}
						data-testid="cart-sheet-mobile"
						onKeyDown={handleMobileSheetKeyDown}
					>
						{/* Visually hidden close button — first focusable target for keyboard/AT users.
						    Matches the focus-on-open useEffect target (mobileCloseButtonRef). */}
						<CloseSheetButton
							ref={mobileCloseButtonRef}
							onClick={handleCloseMobileSheet}
							aria-label={t('component:cartSidebar:sheet:close:label')}
						>
							{t('component:cartSidebar:sheet:close:label')}
						</CloseSheetButton>
						<SwipeHandle />
						{banner}
						{menuReady && <OrderDetailsPanel menuItems={menuItems} />}
					</MobileSheet>
				) : (
					<MobileStickyBar
						ref={mobileStickyBarRef}
						data-testid="cart-sticky-bar-mobile"
						aria-label={t('page:home:reviewOrder:text')}
						aria-haspopup="dialog"
						onClick={() => dispatch(toggleCartDrawer())}
					>
						{t('page:home:reviewOrder:text')}
					</MobileStickyBar>
				)}
			</Box>
		</>
	)
}
